import { useCallback, useMemo, useState } from 'react';

/** 010-1234-5678 / 01012345678 / 1544-3230 형태를 모두 허용 */
const PHONE = /^(0\d{1,2}-?\d{3,4}-?\d{4}|\d{4}-?\d{4})$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const validators = {
  required: (msg) => (v) => (String(v ?? '').trim() ? '' : msg),
  checked: (msg) => (v) => (v === true ? '' : msg),
  phone: (msg) => (v) => {
    const raw = String(v ?? '').replace(/\s/g, '');
    if (!raw) return '';
    return PHONE.test(raw) ? '' : msg;
  },
  email: (msg) => (v) => {
    const raw = String(v ?? '').trim();
    if (!raw) return '';
    return EMAIL.test(raw) ? '' : msg;
  },
};

/** 필드별 검사기 배열을 순서대로 적용해 첫 번째 오류 메시지를 남긴다. */
export function buildValidate(rules) {
  return (values) => {
    const errors = {};
    Object.entries(rules).forEach(([name, checks]) => {
      for (const check of checks) {
        const message = check(values[name], values);
        if (message) {
          errors[name] = message;
          return;
        }
      }
    });
    return errors;
  };
}

/**
 * 제어 컴포넌트 폼 상태 + 검증 + 제출 상태.
 * status: 'idle' | 'submitting' | 'success' | 'error'
 */
/**
 * 값을 다시 포맷하면 캐럿이 맨 뒤로 튄다.
 * 캐럿 앞의 '숫자 개수'를 기준으로 원래 자리를 되찾아 준다.
 */
function restoreCaret(el, digitsBeforeCaret) {
  window.requestAnimationFrame(() => {
    if (document.activeElement !== el) return;
    const text = el.value;
    let i = 0;
    let seen = 0;
    while (i < text.length && seen < digitsBeforeCaret) {
      if (/\d/.test(text[i])) seen += 1;
      i += 1;
    }
    try {
      el.setSelectionRange(i, i);
    } catch (err) {
      /* selectionStart 를 지원하지 않는 입력 타입 */
    }
  });
}

/** format: { 필드명: (값) => 표시값 } — 입력 중 실시간 서식이 필요한 필드만 넣는다. */
export default function useForm({ initialValues, validate, onSubmit, format }) {
  const [values, setValues] = useState(initialValues);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState('idle');

  const errors = useMemo(() => validate(values), [validate, values]);
  const isValid = Object.keys(errors).length === 0;

  const handleChange = useCallback(
    (e) => {
      const el = e.target;
      const { name, type, value, checked } = el;

      if (type === 'checkbox') {
        setValues((prev) => ({ ...prev, [name]: checked }));
      } else {
        const fmt = format && format[name];
        if (fmt) {
          const caret = typeof el.selectionStart === 'number' ? el.selectionStart : value.length;
          const digitsBefore = value.slice(0, caret).replace(/\D/g, '').length;
          setValues((prev) => ({ ...prev, [name]: fmt(value) }));
          restoreCaret(el, digitsBefore);
        } else {
          setValues((prev) => ({ ...prev, [name]: value }));
        }
      }

      // 결과 배너가 떠 있는 동안 다시 입력하면 배너를 지운다.
      setStatus((prev) => (prev === 'success' || prev === 'error' ? 'idle' : prev));
    },
    [format]
  );

  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  }, []);

  /** 아직 건드리지 않은 필드는 제출 전까지 오류를 감춘다. */
  const errorOf = useCallback(
    (name) => ((touched[name] || submitted) && errors[name]) || '',
    [errors, submitted, touched]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitted(true);

      if (!isValid) {
        const first = Object.keys(errors)[0];
        const el = e.target.elements?.[first];
        if (el && typeof el.focus === 'function') el.focus();
        return;
      }

      setStatus('submitting');
      try {
        await onSubmit(values);
        setValues(initialValues);
        setTouched({});
        setSubmitted(false);
        setStatus('success');
      } catch (err) {
        console.error('[offergroup] 폼 제출 실패:', err);
        setStatus('error');
      }
    },
    [errors, initialValues, isValid, onSubmit, values]
  );

  return {
    values,
    errors,
    status,
    isValid,
    submitting: status === 'submitting',
    errorOf,
    handleChange,
    handleBlur,
    handleSubmit,
  };
}
